import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export type LogMessagePart = {
  text: string
  highlight: boolean
}

const DEFAULT_LOG_KEYWORDS = ['ERROR', 'CACHE', 'THINK']

export function splitLogMessageParts(message: string, keywords: string[] = DEFAULT_LOG_KEYWORDS): LogMessagePart[] {
  if (!message) {
    return []
  }
  const normalized = String(message)
  if (!keywords.length) {
    return [{ text: normalized, highlight: false }]
  }
  const escaped = keywords.map((keyword) => keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi')
  const parts = normalized.split(regex).filter((part) => part.length > 0)
  const loweredKeywords = keywords.map((keyword) => keyword.toLowerCase())
  return parts.map((part) => ({
    text: part,
    highlight: loweredKeywords.includes(part.toLowerCase())
  }))
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
    }
  }
}

let turnstileScriptPromise: Promise<void> | null = null

export function loadTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('turnstile script can only load in browser'))
  }

  if (window.turnstile) {
    return Promise.resolve()
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-turnstile-script="true"]') as HTMLScriptElement | null
    if (existing) {
      if (window.turnstile) {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('turnstile script load failed')))
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.dataset.turnstileScript = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('turnstile script load failed'))
    document.head.appendChild(script)
  })

  return turnstileScriptPromise
}

export function resolveAuthToken(storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage) {
  if (!storage?.getItem) {
    return null
  }

  const directKeys = ['token', 'access_token', 'auth_token', 'authToken', 'ozon_token', 'guest_token', 'guestToken']
  for (const key of directKeys) {
    const value = storage.getItem(key)
    if (value) {
      return value
    }
  }

  const authPayload = storage.getItem('auth')
  if (authPayload) {
    try {
      const parsed = JSON.parse(authPayload)
      const token = parsed?.token || parsed?.accessToken || parsed?.access_token
      if (typeof token === 'string' && token) {
        return token
      }
    } catch (error) {
      return null
    }
  }

  return null
}

export async function syncAuthToExtension(
  token: string,
  user: Record<string, any> | null,
  apiBaseUrl?: string
) {
  if (!token || typeof window === 'undefined') {
    return false
  }
  const extensionId = document.documentElement?.getAttribute('data-ozon-erp-extension-id')
  const chromeRuntime = (window as unknown as { chrome?: { runtime?: { sendMessage?: Function } } }).chrome?.runtime
  const sendMessage = chromeRuntime?.sendMessage as ((id: string, payload: Record<string, any>, cb?: () => void) => void) | undefined
  if (!extensionId || !sendMessage) {
    return false
  }

  const apiUrl = resolveApiBaseUrl(apiBaseUrl)
  return await new Promise<boolean>((resolve) => {
    try {
      sendMessage(
        extensionId,
        {
          action: 'syncAuth',
          token,
          user,
          apiUrl,
        },
        () => resolve(true)
      )
    } catch (error) {
      resolve(false)
    }
  })
}

export async function performLogout(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = typeof localStorage === 'undefined'
    ? null
    : localStorage,
  apiBaseUrl?: string
) {
  if (!storage?.getItem) {
    return
  }
  const token = resolveAuthToken(storage)
  if (token && typeof fetch !== 'undefined') {
    try {
      const baseUrl = resolveApiBaseUrl(apiBaseUrl)
      await fetch(`${baseUrl}/api/v1/auth/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    } catch (error) {
      // ignore logout errors
    }
  }

  const keysToRemove = [
    'token',
    'access_token',
    'auth_token',
    'authToken',
    'ozon_token',
    'guest_token',
    'guestToken',
    'auth',
    'guest_user',
  ]
  for (const key of keysToRemove) {
    try {
      storage.removeItem(key)
    } catch (error) {
      // ignore storage errors
    }
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:updated'))
  }
}

const ADMIN_TOKEN_KEY = 'admin_token'

export function resolveAdminToken(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.getItem) {
    return null
  }
  return storage.getItem(ADMIN_TOKEN_KEY)
}

export function saveAdminToken(
  token: string,
  storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.setItem) {
    return
  }
  try {
    storage.setItem(ADMIN_TOKEN_KEY, token)
  } catch (error) {
    // ignore storage errors
  }
}

export function clearAdminToken(
  storage: Pick<Storage, 'removeItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.removeItem) {
    return
  }
  try {
    storage.removeItem(ADMIN_TOKEN_KEY)
  } catch (error) {
    // ignore storage errors
  }
}

export type AuthUserPayload = {
  id?: string
  phone?: string
  email?: string
  avatar?: string | null
  plan?: string
  plan_name?: string
  status?: string
  is_admin?: boolean
  nickname?: string
  username?: string
}

export type AuthPayload = {
  token?: string
  user?: AuthUserPayload
  quota?: Record<string, unknown>
}

export function resolveAuthPayload(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.getItem) {
    return null
  }
  const raw = storage.getItem('auth')
  if (!raw) {
    return null
  }
  try {
    return JSON.parse(raw) as AuthPayload
  } catch (error) {
    return null
  }
}

export function resolveAuthUser(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  const payload = resolveAuthPayload(storage)
  if (payload?.user) {
    return payload.user
  }
  if (!storage?.getItem) {
    return null
  }
  const guestRaw = storage.getItem('guest_user')
  if (!guestRaw) {
    return null
  }
  try {
    const parsed = JSON.parse(guestRaw)
    if (parsed && typeof parsed === 'object') {
      return parsed as AuthUserPayload
    }
  } catch (error) {
    return null
  }
  return null
}

export async function refreshAuthProfile(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = typeof localStorage === 'undefined'
    ? null
    : localStorage,
  apiBaseUrl?: string
) {
  if (!storage?.getItem || typeof fetch === 'undefined') {
    return null
  }
  const token = resolveAuthToken(storage)
  if (!token) {
    return null
  }
  const baseUrl = resolveApiBaseUrl(apiBaseUrl)
  try {
    const response = await fetch(`${baseUrl}/api/v1/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    if (!response.ok) {
      return null
    }
    const payload = await response.json().catch(() => null)
    if (!payload?.user) {
      return null
    }
    const existing = resolveAuthPayload(storage) ?? {}
    const nextPayload = {
      ...existing,
      ...payload,
      token: existing?.token || token,
      user: payload.user,
      quota: payload.quota ?? existing?.quota
    }
    storage.setItem('auth', JSON.stringify(nextPayload))
    return nextPayload
  } catch (error) {
    return null
  }
}

export function buildAuthModalPath(
  pathname: string,
  search: string,
  mode: 'login' | 'signup' | 'forgot' | 'reset'
) {
  const params = new URLSearchParams(search)
  params.set('auth', mode)
  const nextSearch = params.toString()
  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}`
}

export type UserStage = {
  stage: 'guest' | 'new' | 'active'
  isAuthenticated: boolean
  plan: string
  isAdmin: boolean
}

export function resolveUserStage(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): UserStage {
  const token = resolveAuthToken(storage)
  if (!token) {
    return {
      stage: 'guest',
      isAuthenticated: false,
      plan: 'free',
      isAdmin: false
    }
  }

  const user = resolveAuthUser(storage)
  const plan = user?.plan || 'free'
  const isAdmin = Boolean(user?.is_admin)
  const stage = plan && plan !== 'free' ? 'active' : 'new'
  return {
    stage,
    isAuthenticated: true,
    plan,
    isAdmin
  }
}

export type AiConfigStyle = 'professional' | 'creative' | 'concise' | 'custom'
export type AiConfigSeoLevel = 'standard' | 'deep'
export type AiConfig = {
  contentStyle: AiConfigStyle
  seoLevel: AiConfigSeoLevel
  language: 'ru' | 'en'
  enableImageGeneration: boolean
}

export const defaultAiConfig: AiConfig = {
  contentStyle: 'professional',
  seoLevel: 'standard',
  language: 'ru',
  enableImageGeneration: true
}

const AI_CONFIG_STORAGE_KEY = 'ai_config'

export function loadAiConfig(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): AiConfig {
  if (!storage?.getItem) {
    return { ...defaultAiConfig }
  }
  const raw = storage.getItem(AI_CONFIG_STORAGE_KEY)
  if (!raw) {
    return { ...defaultAiConfig }
  }
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') {
      return { ...defaultAiConfig }
    }
    return { ...defaultAiConfig, ...parsed }
  } catch (error) {
    return { ...defaultAiConfig }
  }
}

export function saveAiConfig(
  config: AiConfig,
  storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.setItem) {
    return
  }
  try {
    storage.setItem(AI_CONFIG_STORAGE_KEY, JSON.stringify(config))
  } catch (error) {
    // ignore storage errors
  }
}

export const IMAGE_CREDITS_PER_IMAGE = 2
export const CREDITS_PER_DOLLAR = 10

export type CreditPackId = 'basic' | 'growth' | 'scale'

export type CreditPackPreset = {
  id: CreditPackId
  credits: number
  priceUsd: number
  bonusPercent: number
  highlight?: boolean
}

export const CREDIT_PACK_PRESETS: CreditPackPreset[] = [
  { id: 'basic', credits: 100, priceUsd: 10, bonusPercent: 0 },
  { id: 'growth', credits: 300, priceUsd: 27, bonusPercent: 10, highlight: true },
  { id: 'scale', credits: 800, priceUsd: 68, bonusPercent: 15 }
]

export type SubscriptionPlanId = 'starter' | 'pro' | 'business'

export type SubscriptionPlanPreset = {
  id: SubscriptionPlanId
  monthlyUsd: number
  yearlyUsd: number
  creditsMonthly: number | null
  highlight?: boolean
}

export const SUBSCRIPTION_PLAN_PRESETS: SubscriptionPlanPreset[] = [
  { id: 'starter', monthlyUsd: 19, yearlyUsd: 190, creditsMonthly: 200 },
  { id: 'pro', monthlyUsd: 49, yearlyUsd: 490, creditsMonthly: 560, highlight: true },
  { id: 'business', monthlyUsd: 99, yearlyUsd: 990, creditsMonthly: 1200 }
]

export function creditsToImages(credits?: number | null) {
  if (!credits) {
    return 0
  }
  return Math.floor(credits / IMAGE_CREDITS_PER_IMAGE)
}

export function buildCreditsSummary(credits: number, creditsLabel = 'Credits', imagesLabel = 'Images') {
  const images = creditsToImages(credits)
  return `${credits} ${creditsLabel} · ${images} ${imagesLabel}`
}

export type AgentStatus = 'IDLE' | 'THINKING' | 'ACTIVE' | 'COMPLETE' | 'ERROR'

export function resolveStudioCollectionId(state: unknown, search: string) {
  if (state && typeof state === 'object' && 'collectionId' in state) {
    const value = (state as { collectionId?: unknown }).collectionId
    if (typeof value === 'string' && value) {
      return value
    }
  }

  const params = new URLSearchParams(search)
  return params.get('collectionId') || params.get('collection_id') || ''
}

export function mapStageStatusToAgentStatus(status?: string): AgentStatus {
  if (!status) {
    return 'IDLE'
  }

  if (status === 'completed' || status === 'skipped') {
    return 'COMPLETE'
  }

  if (status === 'failed') {
    return 'ERROR'
  }

  if (status === 'running') {
    return 'ACTIVE'
  }

  return 'IDLE'
}

export type StageStatusMap = Record<string, { status?: string; execution_time_ms?: number; error?: string; updated_at?: string }>

export function toStageStatusMap(response?: { stages?: StageStatusMap | null }) {
  return response?.stages ?? {}
}

export type ImagePlan = {
  index?: number
  prompt?: string
  generated_url?: string
  image_type?: string
  aspect_ratio?: string
  keywords?: string[]
  scene?: string
}

export function normalizeImagePlans(payload?: { image_plans?: ImagePlan[] | null }) {
  return payload?.image_plans ?? []
}

export function buildGeneratePayload(input: {
  marketStatus?: string
  skipImages?: boolean
  useCache?: boolean
  fromStage?: string
  capability?: CapabilityProfile
}) {
  return {
    market_status: input.marketStatus ?? '中等竞争',
    skip_images: input.skipImages ?? false,
    use_cache: input.useCache ?? false,
    from_stage: input.fromStage ?? null,
    capability: input.capability ?? loadCapabilityProfile()
  }
}

export type CapabilityStrategyTier = 'economy' | 'balanced' | 'premium'

export interface CapabilityProfile {
  platform: 'ozon' | 'amazon'
  strategy_tier: CapabilityStrategyTier
  brand_guard: boolean
  language: 'ru' | 'en'
  content_style: 'professional' | 'creative' | 'concise' | 'custom'
  custom_prompt: string
  seo_level: 'standard' | 'deep'
}

export interface CapabilityContext extends CapabilityProfile {
  image_generation: {
    enabled: boolean
    max_images: number
    style_preset: string
  }
  rich_content: {
    enabled: boolean
    theme: string
  }
  model_preset: {
    text: string
    image: string
  }
  summary: string[]
}

const CAPABILITY_STORAGE_KEY = 'capability_profile'

export const defaultCapabilityProfile: CapabilityProfile = {
  platform: 'ozon',
  strategy_tier: 'balanced',
  brand_guard: true,
  language: 'ru',
  content_style: 'professional',
  custom_prompt: '',
  seo_level: 'deep'
}

export function loadCapabilityProfile(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): CapabilityProfile {
  if (!storage?.getItem) {
    return { ...defaultCapabilityProfile }
  }
  const raw = storage.getItem(CAPABILITY_STORAGE_KEY)
  if (!raw) {
    return { ...defaultCapabilityProfile }
  }
  try {
    const parsed = JSON.parse(raw) as Partial<CapabilityProfile> | null
    if (!parsed) {
      return { ...defaultCapabilityProfile }
    }
    return {
      ...defaultCapabilityProfile,
      ...parsed
    }
  } catch (error) {
    return { ...defaultCapabilityProfile }
  }
}

export function saveCapabilityProfile(
  profile: CapabilityProfile,
  storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.setItem) {
    return
  }
  try {
    storage.setItem(CAPABILITY_STORAGE_KEY, JSON.stringify(profile))
  } catch (error) {
    // ignore storage errors
  }
}

export function resolveApiBaseUrl(envBaseUrl?: string, originOverride?: string) {
  const cleanedEnv = envBaseUrl?.trim()
  if (cleanedEnv) {
    return cleanedEnv.replace(/\/$/, '')
  }

  const metaEnv = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL
  if (metaEnv) {
    return metaEnv.replace(/\/$/, '')
  }

  const origin = originOverride ?? (typeof window !== 'undefined' ? window.location.origin : '')
  if (origin) {
    const trimmedOrigin = origin.replace(/\/$/, '')
    try {
      const url = new URL(trimmedOrigin)
      const hostname = url.hostname
      const port = url.port
      const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0' || hostname === '::1'
      const isPrivateIp =
        hostname.startsWith('10.') ||
        hostname.startsWith('192.168.') ||
        /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
      if ((isLocalHost || isPrivateIp) && port && port !== '8000') {
        return `${url.protocol}//${hostname}:8000`
      }
    } catch (error) {
      if (trimmedOrigin.includes(':5666')) {
        return trimmedOrigin.replace(':5666', ':8000')
      }
    }
    return trimmedOrigin
  }

  return 'http://localhost:8000'
}

export function splitOptionValues(value: string) {
  if (!value) return []
  return Array.from(
    new Set(
      value
        .split(/[\s,，;/|、]+/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  )
}

function extractValuesFromSkuName(name: string) {
  const values: string[] = []
  const patterns = [/【([^】]+)】/g, /\[([^\]]+)\]/g, /\(([^)]+)\)/g]
  for (const pattern of patterns) {
    let match = pattern.exec(name)
    while (match) {
      values.push(...splitOptionValues(match[1]))
      match = pattern.exec(name)
    }
  }
  return values
}

function normalizeNumber(value: unknown) {
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.]+/g, '')
    const parsed = Number(cleaned)
    if (!Number.isNaN(parsed)) return parsed
  }
  return null
}

function formatDimensionValue(mmValue: number) {
  const cm = mmValue / 10
  const fixed = Number.isInteger(cm) ? cm.toString() : cm.toFixed(1)
  return fixed
}

function formatWeight(grams: number) {
  const kg = grams / 1000
  const fixed = kg.toFixed(2).replace(/\.?0+$/, '')
  return `${fixed} kg`
}

function humanizeKey(key: string) {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ''))
    .join(' ')
}

function stripUnitSuffix(key: string) {
  const cleaned = key.replace(/_(mm|cm|m|g|kg)$/i, '').replace(/(mm|cm|m|g|kg)$/i, '')
  return cleaned || key
}

export type SkuInsights = {
  colors: string[]
  sizes: string[]
  price: number | null
  stock: number | null
  weightText: string | null
  dimensionsText: string | null
}

export function extractSkuInsights(extractedData?: Record<string, any>): SkuInsights {
  const colors = new Set<string>()
  const sizes = new Set<string>()
  let price: number | null = null
  let stock: number | null = null
  let weightText: string | null = null
  let dimensionsText: string | null = null

  if (!extractedData) {
    return { colors: [], sizes: [], price, stock, weightText, dimensionsText }
  }

  const selectPrice = (value: unknown) => {
    const parsed = normalizeNumber(value)
    if (parsed !== null) {
      price = parsed
    }
  }

  if (extractedData.selected_sku?.price !== undefined) {
    selectPrice(extractedData.selected_sku.price)
  }

  if (price === null && extractedData.price !== undefined) {
    selectPrice(extractedData.price)
  }

  if (price === null && typeof extractedData.price_range === 'string') {
    selectPrice(extractedData.price_range)
  }

  if (extractedData.selected_sku?.stock !== undefined) {
    const parsed = normalizeNumber(extractedData.selected_sku.stock)
    if (parsed !== null) {
      stock = parsed
    }
  }

  const attributeEntries = extractedData.parsed_attributes
    ? Object.entries(extractedData.parsed_attributes)
    : []

  attributeEntries.forEach(([key, value]) => {
    const val = typeof value === 'string' ? value : ''
    if (!val) return
    if (/颜色|color|colour/i.test(key)) {
      splitOptionValues(val).forEach((item) => colors.add(item))
    }
    if (/尺码|尺寸|size/i.test(key)) {
      splitOptionValues(val).forEach((item) => sizes.add(item))
    }
  })

  if (Array.isArray(extractedData.parsed_skus)) {
    extractedData.parsed_skus.forEach((sku: Record<string, any>) => {
      if (price === null && sku?.price !== undefined) {
        selectPrice(sku.price)
      }

      const skuStock = normalizeNumber(sku?.stock)
      if (skuStock !== null) {
        stock = stock === null ? skuStock : Math.max(stock, skuStock)
      }

      const attributes = sku?.attributes && typeof sku.attributes === 'object' ? sku.attributes : null
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          const val = typeof value === 'string' ? value : ''
          if (!val) return
          if (/颜色|color|colour/i.test(key)) {
            splitOptionValues(val).forEach((item) => colors.add(item))
          }
          if (/尺码|尺寸|size/i.test(key)) {
            splitOptionValues(val).forEach((item) => sizes.add(item))
          }
        })
      }

      const variantType = sku?.variant_type || sku?.variantType || sku?.type
      const variantValue = sku?.variant_value || sku?.variantValue || sku?.value
      if (typeof variantType === 'string' && typeof variantValue === 'string') {
        if (/颜色|color|colour/i.test(variantType)) {
          splitOptionValues(variantValue).forEach((item) => colors.add(item))
        }
        if (/尺码|尺寸|size/i.test(variantType)) {
          splitOptionValues(variantValue).forEach((item) => sizes.add(item))
        }
      } else if (typeof sku?.name === 'string') {
        const values = extractValuesFromSkuName(sku.name)
        values.forEach((item) => {
          if (!colors.has(item) && sizes.size === 0) {
            colors.add(item)
          } else {
            sizes.add(item)
          }
        })
      }
    })
  }

  const dimensions = extractedData.parsed_dimensions
  if (dimensions && typeof dimensions === 'object') {
    const lengthMm = normalizeNumber(dimensions.length_mm)
    const widthMm = normalizeNumber(dimensions.width_mm)
    const heightMm = normalizeNumber(dimensions.height_mm)
    const weightG = normalizeNumber(dimensions.weight_g)

    if (lengthMm !== null && widthMm !== null && heightMm !== null) {
      dimensionsText = `${formatDimensionValue(lengthMm)} x ${formatDimensionValue(widthMm)} x ${formatDimensionValue(heightMm)} cm`
    }
    if (weightG !== null) {
      weightText = formatWeight(weightG)
    }
  }

  return {
    colors: Array.from(colors),
    sizes: Array.from(sizes),
    price,
    stock,
    weightText,
    dimensionsText
  }
}

export function buildPreviewImages(preview: { images?: Array<{ storage_path?: string; original_url?: string; url?: string }>; extracted_data?: Record<string, any> }) {
  const images = Array.isArray(preview?.images)
    ? preview.images
        .map((img) => img.storage_path || img.original_url || img.url)
        .filter(Boolean)
    : []

  if (images.length > 0) {
    return images
  }

  const extractedImages = preview?.extracted_data?.images
  if (Array.isArray(extractedImages) && extractedImages.length > 0) {
    return extractedImages.filter(Boolean)
  }

  const skus = Array.isArray(preview?.extracted_data?.parsed_skus) ? preview.extracted_data.parsed_skus : []
  const skuImages = skus
    .map((sku: Record<string, any>) => sku?.image_url || sku?.imageUrl || sku?.image)
    .filter(Boolean)

  if (skuImages.length > 0) {
    return skuImages
  }

  return []
}

export function derivePricingInsights(extractedData?: Record<string, any>, fallbackPrice?: number | null) {
  const prices: number[] = []
  const pushPrice = (value: unknown) => {
    const parsed = normalizeNumber(value)
    if (parsed !== null) {
      prices.push(parsed)
    }
  }

  if (extractedData?.price_range) {
    const rangeValues = String(extractedData.price_range).match(/\d+(?:\.\d+)?/g) || []
    rangeValues.map(Number).forEach((value) => {
      if (!Number.isNaN(value)) {
        prices.push(value)
      }
    })
  }

  if (Array.isArray(extractedData?.parsed_skus)) {
    extractedData.parsed_skus.forEach((sku: Record<string, any>) => {
      pushPrice(sku?.price)
    })
  }

  if (prices.length === 0 && fallbackPrice !== null && fallbackPrice !== undefined) {
    prices.push(fallbackPrice)
  }

  if (prices.length === 0) {
    return { marketAvg: null, suggested: null }
  }

  const min = Math.min(...prices)
  const max = Math.max(...prices)
  const marketAvg = Number(((min + max) / 2).toFixed(2))
  const suggested = Number((marketAvg * 1.1).toFixed(2))

  return { marketAvg, suggested }
}

export function buildAttributeList(extractedData?: Record<string, any>) {
  const attributes = extractedData?.parsed_attributes
  if (!attributes || typeof attributes !== 'object') {
    return []
  }

  const entries = Array.isArray(attributes)
    ? attributes
        .filter((item) => item && typeof item === 'object')
        .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value ?? ''] as [string, string])
    : Object.entries(attributes)

  return entries
    .filter(([key, value]) => typeof value === 'string' && value.trim())
    .filter(([key]) => !/颜色|color|colour|尺码|尺寸|size/i.test(String(key)))
    .map(([key, value]) => ({ label: String(key), value: String(value) }))
}

export function buildHighlightList(extractedData?: Record<string, any>) {
  const criticalInfo = Array.isArray(extractedData?.critical_info) ? extractedData.critical_info : []
  const mapped = criticalInfo
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      title: String(item.type ?? item.title ?? '').trim(),
      detail: String(item.content ?? item.detail ?? '').trim()
    }))
    .filter((item) => item.title && item.detail)

  if (mapped.length > 0) {
    return mapped
  }

  const attributes = extractedData?.parsed_attributes
  if (!attributes || typeof attributes !== 'object') {
    return []
  }

  const entries = Array.isArray(attributes)
    ? attributes
        .filter((item) => item && typeof item === 'object')
        .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value ?? ''] as [string, string])
    : Object.entries(attributes)

  return entries
    .filter(([key, value]) => typeof value === 'string' && value.trim())
    .filter(([key]) => !/颜色|color|colour|尺码|尺寸|size/i.test(String(key)))
    .map(([key, value]) => ({ title: String(key), detail: String(value) }))
}

export function buildFlagList(extractedData?: Record<string, any>) {
  const flags = extractedData?.flags
  if (!flags || typeof flags !== 'object') {
    return []
  }

  const entries = Array.isArray(flags)
    ? flags
        .filter((item) => item && typeof item === 'object')
        .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value] as [string, unknown])
    : Object.entries(flags)

  return entries
    .filter(([key, value]) => Boolean(key) && value !== undefined && value !== null)
    .map(([key, value]) => {
      let normalizedValue: boolean | string | number
      if (typeof value === 'boolean') {
        normalizedValue = value
      } else if (typeof value === 'number') {
        normalizedValue = value
      } else if (typeof value === 'string') {
        const trimmed = value.trim()
        if (trimmed.toLowerCase() === 'true') {
          normalizedValue = true
        } else if (trimmed.toLowerCase() === 'false') {
          normalizedValue = false
        } else {
          normalizedValue = trimmed
        }
      } else {
        normalizedValue = String(value)
      }

      return {
        label: humanizeKey(String(key)),
        value: normalizedValue
      }
    })
}

export function buildDimensionList(extractedData?: Record<string, any>) {
  const dimensions = extractedData?.parsed_dimensions
  if (!dimensions || typeof dimensions !== 'object') {
    return []
  }

  const entries = Array.isArray(dimensions)
    ? dimensions
        .filter((item) => item && typeof item === 'object')
        .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value] as [string, unknown])
    : Object.entries(dimensions)

  return entries
    .filter(([key, value]) => Boolean(key) && value !== undefined && value !== null && value !== '')
    .map(([key, value]) => {
      const keyText = String(key)
      const numeric = normalizeNumber(value)
      const lowerKey = keyText.toLowerCase()
      let formatted = ''

      if (numeric !== null) {
        if (lowerKey.includes('weight') || keyText.includes('重量')) {
          if (lowerKey.includes('g')) {
            formatted = formatWeight(numeric)
          } else if (lowerKey.includes('kg')) {
            formatted = `${numeric} kg`
          } else {
            formatted = `${numeric}`
          }
        } else if (lowerKey.includes('mm')) {
          formatted = `${formatDimensionValue(numeric)} cm`
        } else if (lowerKey.includes('cm') || keyText.includes('厘米')) {
          formatted = `${numeric} cm`
        } else {
          formatted = `${numeric}`
        }
      } else {
        formatted = String(value)
      }

      return {
        label: humanizeKey(stripUnitSuffix(keyText)),
        value: formatted
      }
    })
}

export function buildImageTagList(extractedData?: Record<string, any>) {
  const tags = extractedData?.image_tags
  if (tags && typeof tags === 'object') {
    return Object.entries(tags)
      .map(([key, value]) => ({ key, count: Array.isArray(value) ? value.length : 0 }))
      .filter((item) => item.count > 0)
      .map((item) => ({ label: humanizeKey(item.key), count: item.count }))
  }

  const analysis = Array.isArray(extractedData?.image_analysis) ? extractedData?.image_analysis : []
  if (!analysis) {
    return []
  }

  const counts = new Map<string, number>()
  analysis.forEach((item: Record<string, any>) => {
    const tag = item?.tag
    if (typeof tag === 'string' && tag.trim()) {
      counts.set(tag, (counts.get(tag) || 0) + 1)
    }
  })

  return Array.from(counts.entries()).map(([key, count]) => ({
    label: humanizeKey(key),
    count
  }))
}

export function buildOverviewMetrics(preview: { images?: Array<{ storage_path?: string; original_url?: string; url?: string }>; extracted_data?: Record<string, any> }) {
  const images = Array.isArray(preview?.images) ? preview.images : []
  const extractedData = preview?.extracted_data

  const fallbackImages = Array.isArray(extractedData?.images) ? extractedData.images : []
  const imageCount = images.length > 0 ? images.length : fallbackImages.length

  const attributes = extractedData?.parsed_attributes
  const attributeCount = Array.isArray(attributes)
    ? attributes.length
    : attributes && typeof attributes === 'object'
      ? Object.keys(attributes).length
      : 0

  const skus = Array.isArray(extractedData?.parsed_skus) ? extractedData.parsed_skus : []
  const skusCount = skus.length

  const rawSections = extractedData?.raw_sections
  const sectionsCount =
    rawSections && typeof rawSections === 'object'
      ? Object.values(rawSections).filter((value) => typeof value === 'string' && value.trim()).length
      : 0

  const rawTextLength = typeof extractedData?.raw_page_text === 'string' ? extractedData.raw_page_text.length : 0

  return [
    { key: 'images', value: imageCount },
    { key: 'skus', value: skusCount },
    { key: 'attributes', value: attributeCount },
    { key: 'sections', value: sectionsCount },
    { key: 'rawText', value: rawTextLength }
  ]
}

export function buildCollectStepList(status?: string) {
  const normalized = status || 'pending'
  const steps = [
    { key: 'queued', status: 'pending' },
    { key: 'assigned', status: 'pending' },
    { key: 'collecting', status: 'pending' },
    { key: 'uploading', status: 'pending' },
    { key: 'completed', status: 'pending' }
  ] as Array<{ key: string; status: 'pending' | 'active' | 'done' | 'error' }>

  const markDoneUntil = (index: number) => {
    steps.forEach((step, idx) => {
      if (idx < index) {
        step.status = 'done'
      }
    })
    if (steps[index]) {
      steps[index].status = 'active'
    }
  }

  switch (normalized) {
    case 'pending':
    case 'queued':
      steps[0].status = 'active'
      break
    case 'assigned':
      markDoneUntil(1)
      break
    case 'collecting':
      markDoneUntil(2)
      break
    case 'uploading':
      markDoneUntil(3)
      break
    case 'completed':
      steps.forEach((step) => {
        step.status = 'done'
      })
      break
    case 'failed':
    case 'cancelled':
    case 'expired':
      steps.forEach((step, idx) => {
        if (idx < steps.length - 1) {
          step.status = 'done'
        }
      })
      steps[steps.length - 1].status = 'error'
      break
    default:
      steps[0].status = 'active'
      break
  }

  return steps
}

export function resolveCollectProgress(status?: string, progress?: number | null) {
  if (typeof progress === 'number' && !Number.isNaN(progress)) {
    return Math.max(0, Math.min(100, Math.round(progress)))
  }

  switch (status) {
    case 'assigned':
      return 25
    case 'collecting':
      return 55
    case 'uploading':
      return 80
    case 'completed':
      return 100
    case 'failed':
    case 'cancelled':
    case 'expired':
      return 100
    case 'pending':
    case 'queued':
    default:
      return 10
  }
}

export function resolveCollectOptions(usePolling: boolean) {
  if (usePolling) {
    return { polling: true, raw_mode: true }
  }
  return { raw_mode: true }
}

export function shouldFallbackToPolling(errorPayload?: { detail?: { code?: string }; code?: string }) {
  const code = errorPayload?.detail?.code || errorPayload?.code || ''
  return ['PLUGIN_NOT_CONNECTED', 'NO_AVAILABLE_DEVICE'].includes(code)
}

export function buildSkuStructure(extractedData?: Record<string, any>) {
  if (!Array.isArray(extractedData?.parsed_skus)) {
    return []
  }

  return extractedData.parsed_skus
    .filter((sku: Record<string, any>) => sku && (sku.name || sku.variant_value || sku.variantValue || sku.value))
    .map((sku: Record<string, any>, index: number) => {
      const attributes =
        sku.attributes && typeof sku.attributes === 'object'
          ? Object.entries(sku.attributes)
              .filter(([_, value]) => typeof value === 'string' && value.trim())
              .map(([key, value]) => ({ label: String(key), value: String(value) }))
          : []

      return {
        name: String(sku.name || sku.variant_value || sku.variantValue || sku.value || `SKU ${index + 1}`),
        price: normalizeNumber(sku.price),
        stock: normalizeNumber(sku.stock),
        variantType: String(sku.variant_type || sku.variantType || sku.type || ''),
        variantValue: String(sku.variant_value || sku.variantValue || sku.value || ''),
        attributes
      }
    })
}

export function buildRawSectionList(extractedData?: Record<string, any>) {
  const sections = extractedData?.raw_sections
  if (!sections || typeof sections !== 'object') {
    return []
  }

  return Object.entries(sections)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => {
      if (typeof value === 'string') {
        return { label: key, content: value }
      }
      return { label: key, content: JSON.stringify(value, null, 2) }
    })
    .filter((item) => item.content && item.content.trim())
}
